import { IonContent } from '@ionic/react';
import React from 'react';

import { UiTypography } from '@/components/ui/Typography';
import { DIALOGUE_SUPPORT_EMAIL, S3_BUCKET_URL } from '@/constants';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { canLogEvents } from '@/utils/canLogEvents';
import { generateDownloadLink } from '@/utils/generateDownloadLink';

import {
  CheckIcon,
  ContactSection,
  ContentSection,
  PageStyles,
  Step,
  StepContent,
  StepHeader,
  StepNumber,
  StepsSection,
  StoreLinks,
  TitleSection,
} from './WithSteps.styles';

const WithStepsVariant: React.FC = () => {
  const { user } = useAccount();

  const downloadingAppLink = React.useMemo(() => {
    return generateDownloadLink(user?.id || '');
  }, [user?.id]);

  const fireAmplitude = (storeType: string) => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('download_click', {
        type: storeType,
      });
    }
  };

  return (
    <PageStyles>
      <IonContent>
        <ContentSection>
          <TitleSection>
            <CheckIcon>
              <img
                src="/assets/success-page/with-steps/check-icon.png"
                alt="Success"
              />
            </CheckIcon>
            <UiTypography variant="title-large" align="center">
              Congrats, your purchase
              <br />
              was successful!
            </UiTypography>
          </TitleSection>

          <StepsSection>
            <Step $completed>
              <StepNumber $completed>
                <img
                  src="/assets/success-page/with-steps/check-mark.png"
                  alt="Done"
                />
              </StepNumber>
              <StepContent $completed>
                <UiTypography variant="body-large">
                  Get your premium plan
                </UiTypography>
              </StepContent>
            </Step>

            <Step $active data-has-extra-content="true">
              <StepHeader>
                <StepNumber $active>
                  <UiTypography variant="title-medium">2</UiTypography>
                </StepNumber>
                <UiTypography variant="body-large">
                  Download the app
                </UiTypography>
              </StepHeader>
              <StoreLinks>
                <a
                  href={downloadingAppLink}
                  onClick={() => fireAmplitude('googleplay')}
                >
                  <img
                    src={`${S3_BUCKET_URL}/onboarding/googleplay.png`}
                    alt="Google Play"
                  />
                </a>
                <a
                  href={downloadingAppLink}
                  onClick={() => fireAmplitude('appstore')}
                >
                  <img
                    src={`${S3_BUCKET_URL}/onboarding/appstore.png`}
                    alt="App Store"
                  />
                </a>
              </StoreLinks>
              <UiTypography variant="body-medium" className="email-hint">
                OR download the app via the link we sent to your email,{' '}
                {user?.email} (check Inbox and Spam folders).
              </UiTypography>
            </Step>

            <Step>
              <StepNumber>
                <UiTypography variant="title-medium">3</UiTypography>
              </StepNumber>
              <StepContent>
                <UiTypography variant="body-large">
                  Enjoy unlimited chats, exclusive content from your favorite
                  characters and more! ❤️‍🔥
                </UiTypography>
              </StepContent>
            </Step>
          </StepsSection>

          <ContactSection>
            <UiTypography variant="body-medium" align="center">
              If you have any questions - reach us at{' '}
              <a href={`mailto:${DIALOGUE_SUPPORT_EMAIL}`}>
                <strong>{DIALOGUE_SUPPORT_EMAIL}</strong>
              </a>
            </UiTypography>
          </ContactSection>
        </ContentSection>
      </IonContent>
    </PageStyles>
  );
};

export default WithStepsVariant;
